/**
 * NEDISO 2D Fallback — Pannellum 360 Integration + Static Image-Map
 * Sprint 2, Phase 2.1 + Light/Dark Mode Support (v2, 28.03.2026)
 *
 * For Tier 0 devices (no WebGL or very weak GPU).
 * Provides:
 * 1. Pannellum 360 panoramic viewer (if 360 image available)
 * 2. Static image-map with clickable hotspots (always available)
 * 3. Keyboard-accessible navigation
 * 4. Theme-aware: reads html[data-theme] for Light/Dark
 */
(function () {
  'use strict';

  function getTheme() {
    var dt = document.documentElement.getAttribute('data-theme');
    if (dt === 'light' || dt === 'dark') return dt;
    return window.matchMedia('(prefers-color-scheme: light)').matches ? 'light' : 'dark';
  }

  var ROOMS = {
    lobby: {
      title: 'Nediso Showroom',
      description: 'Ihr Partner fuer Digital Signage und Konferenztechnik',
      links: [
        { label: 'Digital Signage', href: '/collections/digital-signage', x: 20, y: 40 },
        { label: 'Konferenztechnik', href: '/collections/konferenztechnik', x: 50, y: 35 },
        { label: 'Audio & Video', href: '/collections/audio-video', x: 80, y: 40 },
        { label: 'Alle Produkte', href: '/collections/alle-produkte', x: 50, y: 65 },
        { label: 'Komplett-Loesungen', href: '/collections/komplett-loesungen', x: 50, y: 80 }
      ]
    },
    konferenz: {
      title: 'Konferenzraum',
      description: 'Professionelle Konferenztechnik fuer jeden Raum',
      links: [
        { label: 'Videokonferenzsysteme', href: '/collections/videokonferenzsysteme', x: 30, y: 40 },
        { label: 'Audiokonferenz', href: '/collections/audiokonferenz', x: 70, y: 40 },
        { label: 'Kameras', href: '/collections/kameras', x: 50, y: 30 },
        { label: 'Praesentationssysteme', href: '/collections/praesentationssysteme', x: 50, y: 60 }
      ]
    },
    showroom: {
      title: 'Digital Signage Showroom',
      description: 'Displays, LED-Walls und Media Player',
      links: [
        { label: 'Displays', href: '/collections/displays', x: 30, y: 35 },
        { label: 'LED-Walls', href: '/collections/led-walls', x: 70, y: 35 },
        { label: 'Media Player', href: '/collections/media-player', x: 30, y: 65 },
        { label: 'Projektoren', href: '/collections/projektoren', x: 70, y: 65 }
      ]
    },
    control: {
      title: 'Control Room',
      description: 'Netzwerk, Signal Management und Steuerungen',
      links: [
        { label: 'Signal Management', href: '/collections/signal-management', x: 30, y: 40 },
        { label: 'Netzwerk & Infrastruktur', href: '/collections/netzwerk-infrastruktur', x: 70, y: 40 },
        { label: 'Steuerungen', href: '/collections/steuerungen', x: 50, y: 65 }
      ]
    },
    lounge: {
      title: 'Huddle Room & Lounge',
      description: 'Kompakte Loesungen fuer kleine Raeume',
      links: [
        { label: 'Huddle Room Loesungen', href: '/collections/huddle-room', x: 50, y: 35 },
        { label: 'Komplett-Loesungen', href: '/collections/komplett-loesungen', x: 50, y: 55 },
        { label: 'Raumbuchungssysteme', href: '/collections/raumbuchungssysteme', x: 50, y: 75 }
      ]
    }
  };

  /* Theme-aware color tokens */
  function getColors() {
    var isLight = getTheme() === 'light';
    return {
      textPrimary: isLight ? '#1a1d2b' : '#e8e9ed',
      textSecondary: isLight ? 'rgba(26,29,43,.6)' : 'rgba(255,255,255,.6)',
      cardBg: isLight ? 'rgba(0,0,0,.03)' : 'rgba(255,255,255,.04)',
      cardBorder: isLight ? 'rgba(0,0,0,.06)' : 'rgba(255,255,255,.06)',
      accent: '#00a886',
      btnActiveBg: 'rgba(0,168,134,.15)',
      btnActiveColor: isLight ? '#008a6a' : '#00c49a',
      btnActiveBorder: 'rgba(0,168,134,.25)',
      btnInactiveColor: isLight ? 'rgba(0,0,0,.4)' : 'rgba(255,255,255,.5)',
      btnInactiveBorder: isLight ? 'rgba(0,0,0,.08)' : 'rgba(255,255,255,.08)'
    };
  }

  /**
   * Render a static image-map with clickable hotspots
   * This is the pure-HTML fallback that works everywhere
   */
  function renderStaticMap(container, roomKey) {
    var room = ROOMS[roomKey] || ROOMS.lobby;
    var c = getColors();

    var html = '<div class="nediso-fallback-room" style="position:relative;max-width:1200px;margin:0 auto;padding:60px 24px">';
    html += '<h2 style="font-size:32px;font-weight:700;margin:0 0 8px;color:' + c.textPrimary + '">' + room.title + '</h2>';
    html += '<p style="font-size:16px;color:' + c.textSecondary + ';margin:0 0 40px">' + room.description + '</p>';

    /* Grid of collection links */
    html += '<div style="display:grid;grid-template-columns:repeat(auto-fit,minmax(240px,1fr));gap:16px">';
    room.links.forEach(function (link) {
      html += '<a href="' + link.href + '" style="display:flex;align-items:center;padding:20px 24px;background:' + c.cardBg + ';border:1px solid ' + c.cardBorder + ';border-radius:12px;color:' + c.textPrimary + ';text-decoration:none;font-size:15px;font-weight:500;transition:all .2s">';
      html += '<span style="flex:1">' + link.label + '</span>';
      html += '<span style="color:' + c.accent + ';font-size:18px">&rarr;</span>';
      html += '</a>';
    });
    html += '</div>';

    /* Room navigation */
    html += '<div style="margin-top:48px;display:flex;flex-wrap:wrap;gap:8px">';
    Object.keys(ROOMS).forEach(function (key) {
      var isActive = key === roomKey;
      var style = isActive
        ? 'background:' + c.btnActiveBg + ';color:' + c.btnActiveColor + ';border-color:' + c.btnActiveBorder
        : 'background:transparent;color:' + c.btnInactiveColor + ';border-color:' + c.btnInactiveBorder;
      html += '<button class="nediso-fallback-room-btn" data-fallback-room="' + key + '" style="padding:8px 16px;border:1px solid;border-radius:8px;font-size:13px;font-family:inherit;cursor:pointer;' + style + '">';
      html += ROOMS[key].title;
      html += '</button>';
    });
    html += '</div>';

    html += '</div>';
    container.innerHTML = html;

    /* Bind room buttons */
    container.querySelectorAll('[data-fallback-room]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        renderStaticMap(container, btn.getAttribute('data-fallback-room'));
      });
    });
  }

  /**
   * Initialize Pannellum 360 viewer
   * Requires a 360 equirectangular image per room
   */
  function initPannellum(container, roomKey) {
    if (typeof pannellum === 'undefined') {
      renderStaticMap(container, roomKey);
      return;
    }

    /* 360 images would be stored as theme assets */
    /* For now, fall back to static map since images dont exist yet */
    renderStaticMap(container, roomKey);
  }

  /* Re-render on theme change */
  document.addEventListener('nediso:theme-change', function () {
    var fb = document.getElementById('room-fallback');
    if (fb && fb.classList.contains('is-active')) {
      renderStaticMap(fb, _currentRoom || 'lobby');
    }
  });

  var _currentRoom = 'lobby';

  /* === PUBLIC API === */
  window.nedisoFallback = {
    init: function (container, roomKey) {
      container = container || document.getElementById('room-fallback');
      roomKey = roomKey || 'lobby';
      _currentRoom = roomKey;
      renderStaticMap(container, roomKey);
    },
    rooms: ROOMS
  };

  /* Auto-init if on fallback page */
  var fallbackEl = document.getElementById('room-fallback');
  if (fallbackEl && fallbackEl.classList.contains('is-active')) {
    renderStaticMap(fallbackEl, 'lobby');
  }

})();
